import React, { Component } from 'react';
import { PANEL_OPEN_EVENT } from '../../../support/supportEntry';
import { connect } from 'react-redux';
import Products from '../MenuComponents/Products/Products';
import Categories from '../MenuComponents/Categories/Categories';
import Aux from 'hoc/ReactAux/ReactAux';
import * as actions from 'actions/index';
import {
  closeModalErrorPlacedOrder,
  openModalReceiptOnCart,
  closeModalReceiptOnCart,
  closeModalPlacedOrderOnCart,
} from 'actions/Cart';
import ModalCartMessage from 'components/UI/Modal/ModalCartMessage';
import ModalErrorPlacedOrder from 'components/UI/Modal/ModalError';
import ModalSuccessPayment from 'components/UI/Modal/ModalSuccessPayment';
import ModalReceipt from 'components/UI/Modal/ModalReceipt';
import ModalErrorAvailableRestaurant from 'components/UI/Modal/ModalError';
import ProductsDetails from '../MenuComponents/Products/ProductDetails/ProductDetails';
import ErrorHandling from 'hoc/ErroHandling';
import Cart from '../Cart/Cart';
import objectUtils from 'utils/object';
import currencyUtils from 'utils/currency';
import { setSupportContext } from '../../../support/context';
import { registerVoiceOrderHandler } from '../../../support/voiceOrder';
import { cartLines, lineKey } from '../../../support/basketCheck';
import menuService from 'services/menuService';
import { isNarrowMenuViewport } from 'utils/viewport';
import { ERROR_CODES, errorCopy, knownError } from 'utils/userError';

const RESTAURANT_UNAVAILABLE_ERROR = errorCopy(ERROR_CODES.MENU_RESTAURANT_UNAVAILABLE, {
  title: "This restaurant isn't available today",
  guidance: "Choose another restaurant from today's menu.",
});

export class DynamicMenu extends Component {
  state = {
    differentRestaurant: false,
    productStandyBy: null,
    showCategoriesMobile: false,
    handlingError: {
      showError: false,
      errorMessage: '',
    },
  };

  componentDidMount() {
    window.addEventListener(PANEL_OPEN_EVENT, this.closeCategoriesSheet);
    this.unregisterVoiceOrder = registerVoiceOrderHandler(
      'addToBasket',
      this.addVoiceOrderItems
    );
    this.unregisterVoiceDraft = registerVoiceOrderHandler('applyDraft', this.applyVoiceDraft);
    setSupportContext('menu.restaurant', {
      restaurantId: this.props.restaurantSelected && this.props.restaurantSelected.id,
    });
    this.setState({
      differentRestaurant: false,
    });
    if (this.props.restaurantSelected !== null) {
      this.handleSelectFirstCategoryFromRestaurant();
    } else {
      this.props.history.replace('/Menu');
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.restaurantSelected !== this.props.restaurantSelected) {
      setSupportContext('menu.restaurant', {
        restaurantId: this.props.restaurantSelected && this.props.restaurantSelected.id,
      });
      this.handleSelectFirstCategoryFromRestaurant();
    }
  }

  componentWillUnmount() {
    window.removeEventListener(PANEL_OPEN_EVENT, this.closeCategoriesSheet);
    if (this.unregisterVoiceOrder) this.unregisterVoiceOrder();
    if (this.unregisterVoiceDraft) this.unregisterVoiceDraft();
  }

  // Voice ordering (LOW risk): the dishes the server matched on this menu go into the basket for the
  // customer, as a tap on each would. A dish with meal options opens its own sheet instead. Each product is
  // read from the menu service, so the basket gets exactly what the menu shows.
  addVoiceOrderItems = items => {
    const restaurant = this.props.restaurantSelected;
    if (!restaurant) return Promise.resolve({ error: 'Open a restaurant menu to order.' });
    if (!restaurant.isOpen) {
      return Promise.resolve({ error: `${restaurant.name} has closed for orders today.` });
    }
    if (this.props.restaurantOnCart && this.props.restaurantOnCart.id !== restaurant.id) {
      return Promise.resolve({
        error: 'Your basket has dishes from another restaurant. Empty it first, then try again.',
      });
    }
    const currencySymbol = this.getCurrencySymbolForMenu();
    const categories = Array.from(new Set(items.map(item => item.categoryTypeId)));
    return Promise.all(
      categories.map(idCategory =>
        menuService.getProducts(restaurant.id, idCategory, 0).then(
          products => (Array.isArray(products) ? products : []),
          () => []
        )
      )
    ).then(lists => {
      const products = [].concat(...lists);
      const added = [];
      let withOptions = null;
      items.forEach(item => {
        const product = products.find(candidate => candidate.id === item.productId);
        if (!product) return;
        if (product.mealOptions && product.mealOptions.length > 0) {
          withOptions = withOptions || product;
          return;
        }
        for (let i = 0; i < item.qty; i += 1) {
          this.props.onAddProductToCartNow({ product, business: restaurant });
        }
        added.push({ ...item, name: item.name, unitPrice: product.price });
      });
      if (withOptions) this.props.onSelectProduct(withOptions);
      // Adding opens the basket; on a phone its sheet would cover the composer's card and its confirm.
      if (added.length && isNarrowMenuViewport()) this.props.onToggleCart(false);
      if (!added.length) {
        return {
          currencySymbol,
          error: withOptions
            ? `Choose the options for ${withOptions.name} to add it.`
            : "That dish isn't on the menu right now.",
        };
      }
      return {
        added,
        currencySymbol,
        note: withOptions ? `Choose the options for ${withOptions.name} to add it too.` : undefined,
      };
    });
  };

  // Chat and voice ordering on a pilot restaurant (LOW risk): the server's draft is the whole basket after the
  // customer's words, with real product and option ids. The basket is made to match it, as taps on + and − would:
  // lines the draft drops or shrinks are removed, new ones are added with exactly the choices in the draft. A
  // choice the menu no longer offers is not swapped: the line is left out and reported.
  applyVoiceDraft = draft => {
    const restaurant = this.props.restaurantSelected;
    if (!restaurant) return Promise.resolve({ error: 'Open a restaurant menu to order.' });
    if (!restaurant.isOpen) {
      return Promise.resolve({ error: `${restaurant.name} has closed for orders today.` });
    }
    if (this.props.restaurantOnCart && this.props.restaurantOnCart.id !== restaurant.id) {
      return Promise.resolve({
        error: 'Your basket has dishes from another restaurant. Empty it first, then try again.',
      });
    }
    const inCart = (this.props.cart && this.props.cart.products) || [];
    const current = cartLines(inCart);
    const wanted = (draft || []).map(line => ({
      ...line,
      optionIds: (line.optionIds || []).slice().sort((a, b) => a - b),
    }));
    const unitsWithKey = key =>
      inCart.filter(product =>
        lineKey(product.id, (product.mealOptions || []).map(option => option.idMealOption)) === key
      );
    current.forEach(line => {
      const key = lineKey(line.productId, line.optionIds);
      const target = wanted.find(candidate => lineKey(candidate.productId, candidate.optionIds) === key);
      const extra = line.qty - (target ? target.qty : 0);
      unitsWithKey(key)
        .slice(0, Math.max(0, extra))
        .forEach(product => this.props.onDeleteProductFromCart({ product }));
    });
    const toAdd = wanted
      .map(line => {
        const have = current.find(
          candidate => lineKey(candidate.productId, candidate.optionIds) === lineKey(line.productId, line.optionIds)
        );
        return { ...line, missing: line.qty - (have ? have.qty : 0) };
      })
      .filter(line => line.missing > 0);
    const currencySymbol = this.getCurrencySymbolForMenu();
    const categories = Array.from(new Set(toAdd.map(line => line.categoryTypeId)));
    return Promise.all(
      categories.map(idCategory =>
        menuService.getProducts(restaurant.id, idCategory, 0).then(
          products => (Array.isArray(products) ? products : []),
          () => null
        )
      )
    ).then(lists => {
      if (lists.some(list => list === null)) {
        // Never add from an older menu: the customer tries again.
        return { currencySymbol, error: "We couldn't load the menu just now. Please try again." };
      }
      const products = [].concat(...lists);
      const refused = [];
      toAdd.forEach(line => {
        const product = products.find(candidate => candidate.id === line.productId);
        if (!product || product.outOfStock) {
          refused.push(line.name || 'A dish');
          return;
        }
        const mealOptions = [];
        let missingChoice = false;
        line.optionIds.forEach(optionId => {
          const group = (product.mealOptions || []).find(candidate =>
            (candidate.mealOptionItens || []).some(item => item.id === optionId)
          );
          const item = group && group.mealOptionItens.find(candidate => candidate.id === optionId);
          if (!item || item.outOfStock) missingChoice = true;
          else mealOptions.push({ idCategoryType: group.idCategoryType, idMealOption: item.id, price: item.price });
        });
        const required = (product.mealOptions || []).filter(group => group.flagRequired === 'Y');
        if (
          missingChoice ||
          required.some(group => !mealOptions.some(option => option.idCategoryType === group.idCategoryType))
        ) {
          refused.push(line.name || product.name);
          return;
        }
        const unit = {
          id: product.id,
          name: product.name,
          description: product.description,
          price: product.price + mealOptions.reduce((sum, option) => sum + (option.price || 0), 0),
          ...(mealOptions.length ? { mealOptions } : {}),
        };
        for (let i = 0; i < line.missing; i += 1) {
          this.props.onAddProductToCartNow({ product: unit, business: restaurant });
        }
      });
      if (this.state.isMobile) this.props.onToggleCart(false);
      return {
        currencySymbol,
        refused,
        error: refused.length
          ? `${refused.join(', ')} couldn't be added as asked. Nothing was swapped for it.`
          : undefined,
      };
    });
  };

  getCurrencySymbolForMenu = () =>
    currencyUtils.convertNameToSymbol(
      (this.props.restaurantSelected && this.props.restaurantSelected.currency) || 'EUR'
    );

  // The support conversation opened on a small screen: the Categories sheet mustn't cover it.
  closeCategoriesSheet = () => {
    if (this.state.showCategoriesMobile) this.setState({ showCategoriesMobile: false });
  };

  toggleCategoriesNavClass = () => {
    this.props.onToggleCart(false);
    this.props.onToggleRestaurants(false);
    this.setState({
      showCategoriesMobile: !this.state.showCategoriesMobile,
    });
  };

  toggleCart = () => {
    this.props.onToggleCart();
    this.props.onToggleRestaurants(false);
    this.setState({
      showCategoriesMobile: false,
    });
  };

  toggleRestaurants = () => {
    this.props.onToggleCart(false);
    this.props.onToggleRestaurants();
    this.setState({
      showCategoriesMobile: false,
    });
  };

  handleSelectFirstCategoryFromRestaurant = () => {
    if (
      this.props.restaurantSelected !== null &&
      this.props.restaurantSelected.categoryType
    ) {
      let idCategory =
        this.props.restaurantSelected.categoryType.length > 0
          ? this.props.restaurantSelected.categoryType[0].id
          : 0;
      this.handleSelectCategory(idCategory);
    }
  };

  handleSelectCategory = idCategory => {
    // this.props.onFetchingProducts(this.props.restaurantSelected.id, idCategory);

    this.props.onFetchingProducts(
      this.props.restaurantSelected != null
        ? this.props.restaurantSelected.id
        : 0,
      this.props.restaurantSelected != null ? idCategory : 0
    );
  };

  handleSelectProduct = idProduct => {
    if (this.props.restaurantSelected.isOpen) {
      let product = this.props.products.find(product => {
        return product.id === idProduct;
      });
      let isDiferrent = !this.props.restaurantOnCart
        ? false
        : this.props.restaurantOnCart.id !== this.props.restaurantSelected.id;
      this.setState({ differentRestaurant: isDiferrent });
      if (isDiferrent) {
        this.setState({ productStandBy: product });
      } else {
        this.verifyMealOptionsProduct(product);
      }
    } else {
      this.setState({
        handlingError: {
          showError: true,
          errorMessage: knownError(ERROR_CODES.MENU_RESTAURANT_CLOSED, {
          title: "This restaurant is closed now",
          message: 'It has stopped taking orders for today.',
          guidance: 'Choose another restaurant, or order for tomorrow.',
        }),
        },
      });
    }
  };

  handleConfirmOkCartModal = () => {
    this.props.onDeleteAllProductsFromCart();
    this.verifyMealOptionsProduct(this.state.productStandBy);
    this.setState({ differentRestaurant: false });
  };

  verifyMealOptionsProduct = product => {
    if (product.mealOptions && product.mealOptions.length > 0) {
      this.props.onSelectProduct(product);
    } else {
      this.props.onAddProductToCart({
        product: product,
        business: this.props.restaurantSelected,
      });
    }
  };

  getAmount() {
    return objectUtils.get(this.props, 'cart.payInfo.amount', 0);
  }

  getCurrency() {
    return currencyUtils.convertNameToSymbol(
      objectUtils.get(this.props, 'cart.restaurantSelected.currency', 'EUR')
    );
  }

  getSurcharge = () => {
    const isPayTypeCreditCard = payType => payType === 'C';
    const payType = objectUtils.get(
      this.props,
      'cart.payInfo.payType',
      undefined
    );
    if (isPayTypeCreditCard(payType)) {
      return objectUtils.get(this.props, 'cart.campaignRateCC.amount', 0);
    }
    return 0;
  };

  getSubTotal() {
    return objectUtils.get(this.props, 'cart.subtotal', 0);
  }

  getTaxDelivery = () => {
    return !!this.props.restaurantSelected &&
      this.props.restaurantSelected.taxDelivery &&
      this.isDeliverAtHome()
      ? this.props.restaurantSelected.taxDelivery.toFixed(2)
      : Number(0).toFixed(2);
  };

  getCollectionDiscount = () => {
    let campaignRateCollection = objectUtils.get(
      this.props,
      'cart.campaignRateCollection',
      null
    );
    if (campaignRateCollection) {
      let collectionDiscount =
        campaignRateCollection.discountType === 'P'
          ? `% ${campaignRateCollection.amount}`
          : `- ${campaignRateCollection.amount}`;
      return collectionDiscount;
    } else {
      return '';
    }
  };

  isForCollection = () => {
    return objectUtils.get(
      this.props,
      'cart.restaurantSelected.forCollection',
      'N'
    );
  };

  getReceiptData = () => {
    return {
      cart: this.props.cart,
      total: this.getAmount(),
      currency: this.getCurrency(),
      surcharge: this.getSurcharge(),
      subtotal: this.getSubTotal(),
      taxDelivery: this.getTaxDelivery(),
      userDetails: this.props.userDetails,
      orderDeliveryDate: objectUtils.get(
        this.props,
        'cart.orderDeliveryDate',
        undefined
      ),
      collectionDiscount: this.getCollectionDiscount(),
      forCollection: this.isForCollection(),
      address: this.getAddress(),
      tipValue: objectUtils.get(this.props, 'cart.tipValue', 0),
    };
  };

  handleDenyCartModal = () => {
    this.setState({ differentRestaurant: false });
  };

  handleCloseHandlingError = () => {
    this.setState({
      handlingError: { showError: false, errorMessage: '' },
    });
  };

  handleCloseModalPlacedOrderOnCart = () => {
    this.props.closeModalPlacedOrderOnCart(this.props.history);
  };
  handleCloseModalReceiptOnCart = () => {
    this.props.closeModalReceiptOnCart();
    this.props.history.replace('/Menu');
  };

  handleCloseModalNotAvailableRestaurant = () => {
    this.props.onFetchingMenu(this.props.userDetails.idBusiness);
    this.props.onCloseModalAvailableRestaurant();
    this.props.history.replace('/Menu');
  };

  handleCloseModalReceipt = () => {
    this.props.closeModalReceiptOnCart();
    this.props.history.replace('/Menu');
  };

  getAddress = () => {
    return this.props.addresses[0];
  };

  isDeliverAtHome = () => {
    return this.getAddress() != null
      ? this.getAddress().idCustomerDeliveryAddress != null
      : false;
  };

  getModalAvailableRestaurant = () => {
    let modalAvailableRestaurant =
      this.props.restaurantSelected &&
      this.props.restaurantSelected.isAvailable != null &&
      !this.props.restaurantSelected.isAvailable ? (
        <ModalErrorAvailableRestaurant
          showModal={
            this.props.restaurantSelected &&
            !this.props.restaurantSelected.isAvailable
          }
          errorMessage={RESTAURANT_UNAVAILABLE_ERROR}
          actionButton={this.handleCloseModalNotAvailableRestaurant}
          actionButtonTitle="Close"
          closeModal={this.handleCloseModalNotAvailableRestaurant}
        />
      ) : null;
    return modalAvailableRestaurant;
  };

  render() {
    return (
      <Aux>
        <ErrorHandling
          handlingError={this.state.handlingError}
          closedErrorModal={this.handleCloseHandlingError}
        >
          <ProductsDetails />
          <ModalCartMessage
            clickButtonConfirm={this.handleConfirmOkCartModal}
            clickButtonDeny={this.handleDenyCartModal}
            message={
              <strong>
                Note! You can only order from <br /> one restaurant at a time.
                <br />
                Clear current basket to add?
              </strong>
            }
            showModal={this.state.differentRestaurant}
          />
          <ModalErrorPlacedOrder
            style={{ zIndex: '99999', position: 'absolute', margin: '5% 40%' }}
            styleContent={{ background: 'gainsboro' }}
            closeModal={this.props.closeModalErrorPlacedOrder}
            showModal={this.props.modalErrorPlacedOrder}
            errorMessage={this.props.checkoutError || this.props.errorMessage}
          />
          {this.getModalAvailableRestaurant()}
          <ModalSuccessPayment
            descriptionSuccess={this.props.cart.descriptionSuccess}
            isInvoice={this.props.cart.isInvoice}
            closeModal={this.handleCloseModalPlacedOrderOnCart}
            showModal={this.props.showPlacedModal}
            openReceipt={this.props.openModalReceiptOnCart}
          />
          <ModalReceipt
            showModal={this.props.showReceiptModal}
            closeModal={this.handleCloseModalReceiptOnCart}
            data={this.getReceiptData()}
          />
          <div className="main-content">
            <Categories
              categoriesType={this.props.categoriesRestaurant}
              selectCategory={this.handleSelectCategory}
              showCategoriesMobile={this.state.showCategoriesMobile}
              toggleCategoriesNavClass={this.toggleCategoriesNavClass}
              showRestaurantsMobile={this.props.showRestaurantsMobile}
              toggleRestaurants={this.toggleRestaurants}
              checkout={this.props.checkout}
              toggleCart={this.toggleCart}
            />
            <Products
              isPopularDish={false}
              checkout={this.props.checkout}
              selectProduct={this.handleSelectProduct}
              products={this.props.products}
              business={this.props.restaurantSelected}
            />
            <Cart />
          </div>
        </ErrorHandling>
      </Aux>
    );
  }
}

const mapStateToProps = state => {
  return {
    userDetails: state.authenticationReducer.userDetails,
    restaurantSelected: state.menuReducer.restaurantSelected,
    products: state.menuReducer.products,
    showRestaurantsMobile: state.menuReducer.showRestaurantsMobile,
    cart: state.cartReducer.cart,
    categoriesRestaurant: state.menuReducer.categoriesRestaurant,
    checkout: state.cartReducer.cart.checkout,
    showPlacedModal: state.cartReducer.cart.showPlacedModal,
    showReceiptModal: state.cartReducer.cart.showReceiptModal,
    restaurantOnCart: state.cartReducer.cart.restaurantSelected,
    modalErrorPlacedOrder: state.cartReducer.cart.modalErrorPlacedOrder,
    errorMessage: state.cartReducer.cart.errorLimit,
    checkoutError: state.cartReducer.cart.checkoutError,
    addresses: state.menuReducer.addresses,
  };
};

const mapDispatchToProps = dispatch => {
  return {
    onFetchingMenu: idBusiness => dispatch(actions.fetchingMenu(idBusiness)),
    onFetchingProducts: (idRestaurant, idCategory) =>
      dispatch(actions.fetchingProducts(idRestaurant, idCategory)),
    onSelectProduct: product => dispatch(actions.selectProduct(product)),
    onAddProductToCart: product => dispatch(actions.addProductToCart(product)),
    onAddProductToCartNow: product => dispatch(actions.addProductToCartNow(product)),
    onDeleteProductFromCart: payload => dispatch(actions.deleteProductFromCart(payload)),
    onDeleteAllProductsFromCart: () =>
      dispatch(actions.deleteAllProductsFromCart()),
    onToggleCart: state => dispatch(actions.toggleCart(state)),
    onToggleRestaurants: state => dispatch(actions.toggleRestaurants(state)),
    onCloseModalAvailableRestaurant: () =>
      dispatch(actions.closeModalAvailableRestaurant()),
    cartToggleShowModalReceipt: isClosing =>
      dispatch(actions.cartToggleShowModalReceipt(isClosing)),
    closeModalErrorPlacedOrder: () => dispatch(closeModalErrorPlacedOrder()),
    closeModalPlacedOrderOnCart: history =>
      dispatch(closeModalPlacedOrderOnCart(history)),
    closeModalReceiptOnCart: () => dispatch(closeModalReceiptOnCart()),
    openModalReceiptOnCart: () => dispatch(openModalReceiptOnCart()),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(DynamicMenu);



// WEBPACK FOOTER //
// ./src/containers/Menu/DynamicMenu/DynamicMenu.js